import { useEffect, useRef, useState } from 'react';
import styles from './ExplanationApplyModal.module.css';
import { postExplanationApply } from '../../../api/explanationService';
import { getSggList, getSidoList } from '../../../api/regionService';

function cx(...classes) {
  return classes.filter(Boolean).join(' ');
}

const DAY_NAMES = ['일', '월', '화', '수', '목', '금', '토'];
const STARTUP_KIND_OPTIONS = [
  { code: 'STUDY', label: '공부방' },
  { code: 'CLASS', label: '교습소' },
  { code: 'ACADEMY', label: '학원' },
];

const INITIAL_FORM = {
  name: '',
  startType: '', // 'NEW' | 'CONVERT' — CODE_MST(CD_GROUP=BRD_EXPLANATION_NEW_PARTICIPANT, CD_TYPE=START_TYPE_CD)와 동일한 값
  sidoCd: '', // 희망 창업 지역 시/도 — inquiry 모듈과 동일한 CODE_MST(CD_GROUP=REGION) 코드 체계
  sggCd: '', // 희망 창업 지역 시/군/구
  startupKinds: [], // STARTUP_KIND_OPTIONS의 code 배열 — CODE_MST(CD_TYPE=STARTUP_KIND_CD)와 동일한 값
  message: '',
};

/**
 * [모듈 3-부속] 설명회 신청서 팝업 — Schedule.jsx의 캘린더에서 특정 회차(일정 카드)를
 * 클릭하면 열린다. 실제 구글폼("비상교육 잉글리시팝 사업설명회 신청서")의 문항 구성을
 * 그대로 옮긴 것이며, 문항 번호(1,2,4,5,6,9,10)도 원본 그대로 유지했다 — 3/7/8번은
 * 원본 폼에 없던(조건부이거나 존재하지 않는) 번호라 이 팝업에도 없다.
 *
 * 2026-08-05: POST /api/explanation-applies(ExplanationApplyController)로 실제 제출을
 * 연결했다 (DB 테이블: BRD_EXPLANATION_NEW_PARTICIPANT). expIdx로 "어떤 회차에 신청했는지"를
 * 함께 보낸다 — Schedule.jsx의 openEventModal에서 클릭한 이벤트 객체에 expIdx가 이미
 * 포함돼 있다(useSchedule.js가 ExplanationResponse.expIdx를 그대로 넘겨준다).
 * "5. 희망 지역"은 자유 텍스트가 아니라 inquiry 신청서(가맹문의/본부지원)와 동일한
 * regionService(GET /api/regions/sido, /api/regions/sgg?sidoCd=)를 그대로 재사용해
 * 시/도 → 시/군/구 순서로 선택하게 한다.
 *
 * 원본 구글폼에는 상단에 "설문 기간(2026.06.23 11:02 ~ 제한없음)" 배지가 있었지만,
 * 여기서는 그 대신 "클릭한 회차 정보(지역/일시/대면·비대면)"를 보여주는 것으로 대체했다
 * — 사용자가 어떤 설명회를 신청하는지 팝업만 보고 바로 알 수 있어야 하기 때문.
 *
 * props:
 *   event  : Schedule.jsx에서 클릭한 일정 객체 { d, mode, t, time, place, expIdx, month, year } | null
 *            (null이면 배경만 유지한 채 닫힌 상태로 렌더링 — 닫힘 트랜지션 중 내용이
 *            먼저 사라지지 않도록 마지막 event는 displayEvent에 남겨둔다)
 *   onClose: 닫기 콜백
 */
export default function ExplanationApplyModal({ event, onClose }) {
  const isOpen = !!event;
  const [displayEvent, setDisplayEvent] = useState(event);
  const [form, setForm] = useState(INITIAL_FORM);
  const [phone1, setPhone1] = useState('');
  const [phone2, setPhone2] = useState('');
  const [phone3, setPhone3] = useState('');
  const [sidoList, setSidoList] = useState([]);
  const [sggList, setSggList] = useState([]);
  const [privacyAgree, setPrivacyAgree] = useState(null); // null=미선택, true=동의, false=비동의
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const phone2Ref = useRef(null);
  const phone3Ref = useRef(null);

  useEffect(() => {
    getSidoList()
      .then(setSidoList)
      .catch(() => setSidoList([]));
  }, []);

  // 새 회차를 열 때만 폼을 초기화한다 (닫히는 트랜지션 중에는 이전 값을 그대로 보여줌).
  useEffect(() => {
    if (event) {
      setDisplayEvent(event);
      setForm(INITIAL_FORM);
      setPhone1('');
      setPhone2('');
      setPhone3('');
      setSggList([]);
      setPrivacyAgree(null);
      setError('');
      setSubmitting(false);
      setSubmitted(false);
    }
  }, [event]);

  useEffect(() => {
    const onKeydown = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', onKeydown);
    return () => document.removeEventListener('keydown', onKeydown);
  }, [isOpen, onClose]);

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const handlePhonePart = (setPart, maxLen, nextRef) => (e) => {
    const digits = e.target.value.replace(/\D/g, '').slice(0, maxLen);
    setPart(digits);
    if (digits.length === maxLen && nextRef?.current) {
      nextRef.current.focus();
    }
  };

  const handleSidoChange = async (e) => {
    const sidoCd = e.target.value;
    setForm((prev) => ({ ...prev, sidoCd, sggCd: '' }));
    if (!sidoCd) {
      setSggList([]);
      return;
    }
    try {
      setSggList(await getSggList(sidoCd));
    } catch {
      setSggList([]);
    }
  };

  const toggleStartupKind = (code) => {
    setForm((prev) => {
      const has = prev.startupKinds.includes(code);
      return {
        ...prev,
        startupKinds: has ? prev.startupKinds.filter((k) => k !== code) : [...prev.startupKinds, code],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (
      !form.name.trim() ||
      phone1.length !== 3 ||
      phone2.length !== 4 ||
      phone3.length !== 4 ||
      !form.startType ||
      !form.sidoCd ||
      !form.sggCd ||
      form.startupKinds.length === 0
    ) {
      setError('필수 항목을 모두 입력해 주세요.');
      return;
    }
    if (privacyAgree !== true) {
      setError('개인정보 수집 및 이용에 동의하셔야 신청이 가능합니다.');
      return;
    }

    setSubmitting(true);
    try {
      await postExplanationApply({
        expIdx: displayEvent.expIdx,
        name: form.name,
        phone: `${phone1}-${phone2}-${phone3}`,
        startTypeCd: form.startType,
        sidoCd: form.sidoCd,
        sggCd: form.sggCd,
        startupKindCds: form.startupKinds,
        content: form.message,
        privacyAgree: true,
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.message || '신청 접수 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
    } finally {
      setSubmitting(false);
    }
  };

  if (!displayEvent) return null;

  const weekday = DAY_NAMES[new Date(displayEvent.year, displayEvent.month - 1, displayEvent.d).getDay()];
  const modeLabel = displayEvent.mode === 'on' ? '비대면' : '대면';

  return (
    <div
      className={cx(styles.backdrop, isOpen && styles.open)}
      aria-hidden={!isOpen}
    >
      <div className={styles.modal} role="dialog" aria-modal="true" aria-label="사업설명회 신청서">
        {submitted ? (
          <div className={styles.doneWrap}>
            <div className={styles.doneIco} aria-hidden="true">
              ✓
            </div>
            <h3>신청이 접수되었습니다</h3>
            <p>
              {form.name} 님, {displayEvent.month}월 {displayEvent.d}일({weekday})
              {displayEvent.time ? ` ${displayEvent.time}` : ''} 설명회 신청이 정상적으로 접수되었습니다.
              <br />
              담당자가 확인 후 빠르게 연락드리겠습니다.
            </p>
            <button type="button" className={styles.submitBtn} onClick={onClose}>
              닫기
            </button>
          </div>
        ) : (
          <>
            <div className={styles.head}>
              <div>
                <h3>비상교육 잉글리시팝 사업설명회 신청서</h3>
                <p className={styles.headDesc}>
                  초보 원장님도 안심하고 시작할 수 있는 잉글리시팝의 창업 솔루션!
                  <br />
                  상권 분석, 운영 노하우, 각종 가맹점 지원까지 밀착 관리합니다!
                  <br />
                  지금 상담 신청하시고 (주)비상교육이 제안하는 1:1 맞춤형 창업 플랜을 받아보세요.
                </p>
              </div>
              <button type="button" className={styles.closeBtn} aria-label="닫기" onClick={onClose}>
                ✕
              </button>
            </div>

            <div className={styles.sessionInfo}>
              <span className={cx(styles.sessionTag, styles[displayEvent.mode])}>{modeLabel}</span>
              <b>
                {displayEvent.month}월 {displayEvent.d}일({weekday})
                {displayEvent.time ? ` ${displayEvent.time}` : ''}
              </b>
              <span className={styles.sessionPlace}>
                {displayEvent.t}
                {displayEvent.place ? ` / ${displayEvent.place}` : ''}
                {displayEvent.tel ? ` (문의 : ${displayEvent.tel})` : ''}
              </span>
            </div>

            <form className={styles.form} onSubmit={handleSubmit} noValidate>
              <div className={styles.field}>
                <label htmlFor="ea-name">
                  1. 신청자분의 성함을 작성해주세요. <span className={styles.req}>*</span>
                </label>
                <input
                  id="ea-name"
                  type="text"
                  maxLength={20}
                  placeholder="답변을 입력해주세요."
                  value={form.name}
                  onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
                />
              </div>

              <div className={styles.field}>
                <label htmlFor="ea-phone1">
                  2. 신청자분의 연락처를 작성해주세요. <span className={styles.req}>*</span>
                </label>
                <div className={styles.phoneRow}>
                  <input
                    id="ea-phone1"
                    type="tel"
                    inputMode="numeric"
                    maxLength={3}
                    placeholder="010"
                    value={phone1}
                    onChange={handlePhonePart(setPhone1, 3, phone2Ref)}
                  />
                  <span className={styles.phoneDash}>-</span>
                  <input
                    ref={phone2Ref}
                    type="tel"
                    inputMode="numeric"
                    maxLength={4}
                    placeholder="1234"
                    value={phone2}
                    onChange={handlePhonePart(setPhone2, 4, phone3Ref)}
                  />
                  <span className={styles.phoneDash}>-</span>
                  <input
                    ref={phone3Ref}
                    type="tel"
                    inputMode="numeric"
                    maxLength={4}
                    placeholder="5678"
                    value={phone3}
                    onChange={handlePhonePart(setPhone3, 4, null)}
                  />
                </div>
              </div>

              <div className={styles.field}>
                <label>
                  4. 창업 형태를 선택해주세요. <span className={styles.req}>*</span>
                </label>
                <label className={cx(styles.optionCard, form.startType === 'NEW' && styles.optionSelected)}>
                  <input
                    type="radio"
                    name="ea-startType"
                    className={styles.optionInput}
                    checked={form.startType === 'NEW'}
                    onChange={() => setForm((prev) => ({ ...prev, startType: 'NEW' }))}
                  />
                  <span className={styles.optionCheck} aria-hidden="true" />
                  <span>신규 창업</span>
                </label>
                <label className={cx(styles.optionCard, form.startType === 'CONVERT' && styles.optionSelected)}>
                  <input
                    type="radio"
                    name="ea-startType"
                    className={styles.optionInput}
                    checked={form.startType === 'CONVERT'}
                    onChange={() => setForm((prev) => ({ ...prev, startType: 'CONVERT' }))}
                  />
                  <span className={styles.optionCheck} aria-hidden="true" />
                  <span>브랜드 전환 (기존 운영 중인 학원이 있을 경우)</span>
                </label>
              </div>

              <div className={styles.field}>
                <label htmlFor="ea-sido">
                  5. 희망 창업 지역을 선택해주세요. <span className={styles.req}>*</span>
                </label>
                <div className={styles.regionSelectRow}>
                  <select id="ea-sido" value={form.sidoCd} onChange={handleSidoChange}>
                    <option value="">시/도 선택</option>
                    {sidoList.map((sido) => (
                      <option key={sido.cd} value={sido.cd}>
                        {sido.cdNm}
                      </option>
                    ))}
                  </select>
                  <select
                    id="ea-sgg"
                    value={form.sggCd}
                    onChange={(e) => setForm((prev) => ({ ...prev, sggCd: e.target.value }))}
                    disabled={!form.sidoCd}
                  >
                    <option value="">시/군/구 선택</option>
                    {sggList.map((sgg) => (
                      <option key={sgg.cd} value={sgg.cd}>
                        {sgg.cdNm}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className={styles.field}>
                <label>
                  6. 희망 창업 종류를 선택해주세요. (복수선택) <span className={styles.req}>*</span>
                </label>
                {STARTUP_KIND_OPTIONS.map((opt) => (
                  <label
                    key={opt.code}
                    className={cx(styles.optionCard, form.startupKinds.includes(opt.code) && styles.optionSelected)}
                  >
                    <input
                      type="checkbox"
                      className={styles.optionInput}
                      checked={form.startupKinds.includes(opt.code)}
                      onChange={() => toggleStartupKind(opt.code)}
                    />
                    <span className={cx(styles.optionCheck, styles.optionCheckSquare)} aria-hidden="true" />
                    <span>{opt.label}</span>
                  </label>
                ))}
              </div>

              <div className={styles.field}>
                <label htmlFor="ea-message">9. 문의사항이 있으실 경우 작성해주세요.</label>
                <textarea
                  id="ea-message"
                  rows={3}
                  maxLength={300}
                  placeholder="답변을 입력해주세요."
                  value={form.message}
                  onChange={(e) => setForm((prev) => ({ ...prev, message: e.target.value }))}
                />
                <span className={styles.charCount}>{form.message.length}/300</span>
              </div>

              <div className={styles.field}>
                <label>
                  10. 개인정보 수집 및 이용 동의 <span className={styles.req}>*</span>
                </label>
                <p className={styles.consentHint}>동의하지 않을 경우, 설문 참여가 제한될 수 있습니다.</p>
                <div className={styles.consentBox}>
                  <div className={styles.consentRow}>
                    <span>수집 및 이용하는 개인정보 항목</span>
                    <b>이름, 연락처, 운영 학원명, 지역</b>
                  </div>
                  <div className={styles.consentRow}>
                    <span>수집 및 이용 목적</span>
                    <b>비상교육 잉글리시팝 사업설명회 일정 및 가맹 안내</b>
                  </div>
                  <div className={styles.consentRow}>
                    <span>보유 및 이용기간</span>
                    <b className={styles.consentHighlight}>수집 후, 1년 보관</b>
                  </div>
                </div>
                <div className={styles.agreeToggle}>
                  <button
                    type="button"
                    className={cx(styles.agreeBtn, privacyAgree === true && styles.agreeBtnActive)}
                    onClick={() => setPrivacyAgree(true)}
                  >
                    동의합니다
                  </button>
                  <button
                    type="button"
                    className={cx(styles.agreeBtn, privacyAgree === false && styles.agreeBtnActiveOff)}
                    onClick={() => setPrivacyAgree(false)}
                  >
                    동의하지 않습니다
                  </button>
                </div>
              </div>

              {error && <p className={styles.errorMsg}>{error}</p>}

              <div className={styles.actions}>
                <button type="button" className={styles.cancelBtn} onClick={onClose} disabled={submitting}>
                  닫기
                </button>
                <button type="submit" className={styles.submitBtn} disabled={submitting}>
                  {submitting ? '제출 중...' : '신청하기'}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
